import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent, CartUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { themeQuantityControlsDisabled } from '@theme/utilities';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {{variant_id: number, quantity: number}} CartItem
 */

/**
 * @typedef {{items?: CartItem[]}} Cart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLElement} stickyQuantityValue - Sticky quantity stepper value
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the product details area is out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber', 'stickyQuantityValue'];

  /** @type {IntersectionObserver | null} */
  #detailsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  /** @type {boolean} */
  #detailsInView = true;

  /** @type {boolean} */
  #stickyQuantityEnabled = false;

  /** @type {boolean} */
  #updatingCartQuantity = false;

  /** @type {number | null} */
  #queuedCartQuantity = null;

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();
    this.#applyStickyPriceStyles();
    this.#syncStickyColorSchemeFromMainButton();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    this.#getInitialQuantity();
    void this.#syncStickyQuantityFromCart();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#detailsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#abortController.abort();
  }

  /**
   * Sets up the IntersectionObserver to watch product details visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const section = this.closest('.shopify-section');
    const detailsBlock =
      section?.querySelector('[data-testid="product-information-details"]') ??
      productForm.closest('.buy-buttons-block') ??
      productForm;
    if (!detailsBlock) return;

    // In some themes the footer element selector may differ.
    // Sticky behavior should still work without footer-based hiding.
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');

    // Observer for product details visibility
    this.#detailsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      this.#detailsInView = entry.isIntersecting;

      // Show sticky bar any time details are not visible (above OR below viewport)
      if (!entry.isIntersecting && !this.#isStuck && !this.#hiddenByBottom) {
        this.#showStickyBar();
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    // Observer for footer visibility - hides sticky bar at page bottom
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          // Footer out of view - restore sticky bar when details are still out of view
          if (!this.#detailsInView) {
            this.#showStickyBar();
          }
          this.#hiddenByBottom = false;
        }
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#detailsIntersectionObserver.observe(detailsBlock);
    if (footer) {
      this.#mainBottomObserver.observe(footer);
    }
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = () => {
    if (!this.#targetAddToCartButton) return;
    // Never forward clicks for structurally unavailable variants ($0 price,
    // sold out). The server-rendered disabled attribute prevents the sticky
    // button from firing in the first place, but this is a defence-in-depth
    // check in case the disabled attribute is cleared by a morph/re-render.
    if (
      this.refs.addToCartButton.dataset.unavailable === 'true' ||
      this.#targetAddToCartButton.dataset.unavailable === 'true'
    ) {
      return;
    }

    this.refs.addToCartButton.dataset.loading = 'true';
    this.refs.addToCartButton.disabled = true;

    this.#syncMainQuantityInput();
    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();
  };

  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;

    // Get the new sticky add to cart HTML from the server response
    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    // Store current visibility state before morphing
    const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    // Morph the entire sticky bar content
    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    // Restore visibility state after morphing
    this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
    this.dataset.variantAvailable = variantAvailable;

    // Update the dataset attributes with new variant info
    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
    }

    // Re-cache the target add to cart button after morphing
    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    }

    // Re-apply sticky price styles after morph replaces DOM nodes
    this.#applyStickyPriceStyles();
    this.#syncStickyColorSchemeFromMainButton();
    void this.#syncStickyQuantityFromCart();

    if (variant == null) {
      this.#handleVariantUnavailable();
    }
    // Restore the current quantity display if needed
    this.#updateButtonText();
  };

  /**
   * Handles variant selected events
   * @param {CustomEvent} event - The variant selected event
   */
  #handleVariantSelected = (event) => {
    // The variant update event will follow and handle all updates via morph
    // We just update the dataset here for tracking
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CustomEvent} event - The cart event
   */
  #handleCartAddComplete = async (event) => {
    if (event.type === ThemeEvents.cartUpdate) {
      const cart = /** @type {Cart | undefined} */ (event.detail?.resource);
      if (cart?.items) {
        this.#updateStickyQuantityFromCart(cart);
      } else {
        await this.#syncStickyQuantityFromCart();
      }
    }

    if (event.type === ThemeEvents.cartError) {
      // Roll back optimistic state if add fails.
      await this.#syncStickyQuantityFromCart();
    }

    // Reset the puppet flag after cart operation
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }

    this.refs.addToCartButton.dataset.loading = 'false';
    // Preserve structural unavailability (e.g. $0 price) that the server marked
    // with data-unavailable. Otherwise clearing the loading state would silently
    // re-enable a button that should never be clickable for this variant.
    if (this.refs.addToCartButton.dataset.unavailable !== 'true') {
      this.refs.addToCartButton.disabled = false;
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Handles sticky quantity increase.
   * @param {Event} event - The click event.
   */
  increaseStickyQuantity = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    event.preventDefault();
    if (themeQuantityControlsDisabled()) return;
    void this.#updateCartQuantity(this.#currentQuantity + 1);
  };

  /**
   * Handles sticky quantity decrease.
   * @param {Event} event - The click event.
   */
  decreaseStickyQuantity = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    event.preventDefault();
    if (themeQuantityControlsDisabled()) return;
    void this.#updateCartQuantity(Math.max(0, this.#currentQuantity - 1));
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  // Helper methods
  /**
   * Syncs sticky bar color-* class from the main PDP add-to-cart context.
   * This keeps sticky button tokens aligned with the exact PDP scheme in use.
   */
  #syncStickyColorSchemeFromMainButton() {
    const stickyBar = this.refs.stickyBar;
    const mainButton = this.#targetAddToCartButton;
    if (!stickyBar || !mainButton) return;

    const currentColorClass = Array.from(stickyBar.classList).find((className) => className.startsWith('color-'));
    const sourceColorClass = this.#findClosestColorClass(mainButton);
    if (!sourceColorClass) return;

    if (currentColorClass && currentColorClass !== sourceColorClass) {
      stickyBar.classList.remove(currentColorClass);
    }
    if (!stickyBar.classList.contains(sourceColorClass)) {
      stickyBar.classList.add(sourceColorClass);
    }
  }

  /**
   * Finds nearest ancestor class token that starts with "color-".
   * @param {Element} element
   * @returns {string | null}
   */
  #findClosestColorClass(element) {
    let current = element;
    while (current) {
      const colorClass = Array.from(current.classList).find((className) => className.startsWith('color-'));
      if (colorClass) return colorClass;
      const parent = current.parentElement;
      if (!parent) break;
      current = parent;
    }
    return null;
  }

  /**
   * Applies sticky-specific price variables directly on the price display element.
   * This keeps sizing stable even if stylesheet order or DOM placement changes.
   */
  #applyStickyPriceStyles() {
    const priceDisplay = /** @type {HTMLElement | null} */ (
      this.querySelector('.bunnings-price-display.sticky-add-to-cart__price-display')
    );
    if (!priceDisplay) return;

    priceDisplay.style.setProperty('--bunnings-price-size', '1.75rem');
    priceDisplay.style.setProperty('--bunnings-price-subtitle-size', 'var(--text-xsmall)');

    const stickyPrice = /** @type {HTMLElement | null} */ (
      this.querySelector('.sticky-add-to-cart__price-value')
    );
    if (stickyPrice) {
      stickyPrice.style.fontWeight = '400';
    }

    const currencyValues = this.querySelectorAll('.sticky-add-to-cart__price-display .bunnings-price-display__currency');
    currencyValues.forEach((currencyValue) => {
      const currencyElement = /** @type {HTMLElement} */ (currencyValue);
      currencyElement.style.fontSize = '1.75rem';
      currencyElement.style.fontWeight = '400';
      currencyElement.style.paddingTop = '0';
      currencyElement.style.alignSelf = 'flex-start';
      currencyElement.style.lineHeight = '1';
    });

    const majorValues = this.querySelectorAll('.sticky-add-to-cart__price-display .bunnings-price-display__major');
    majorValues.forEach((majorValue) => {
      const majorElement = /** @type {HTMLElement} */ (majorValue);
      majorElement.style.fontSize = '1.75rem';
      majorElement.style.fontWeight = '400';
      majorElement.style.lineHeight = '1';
    });

    const minorValues = this.querySelectorAll('.sticky-add-to-cart__price-display .bunnings-price-display__minor');
    minorValues.forEach((minorValue) => {
      const minorElement = /** @type {HTMLElement} */ (minorValue);
      minorElement.style.fontSize = '1rem';
      minorElement.style.fontWeight = '400';
      minorElement.style.paddingTop = '0';
      minorElement.style.alignSelf = 'flex-start';
      minorElement.style.lineHeight = '1';
    });
  }

  /**
   * Shows or hides sticky quantity controls.
   * @param {boolean} enabled - Whether controls should be visible.
   */
  #setStickyQuantityEnabled(enabled) {
    if (themeQuantityControlsDisabled()) {
      this.#stickyQuantityEnabled = false;
      const quantityContainer = /** @type {HTMLElement | null} */ (this.querySelector('[data-sticky-quantity]'));
      quantityContainer?.setAttribute('hidden', '');
      this.refs.addToCartButton.style.display = '';
      return;
    }

    this.#stickyQuantityEnabled = enabled;
    const quantityContainer = /** @type {HTMLElement | null} */ (this.querySelector('[data-sticky-quantity]'));
    if (!quantityContainer) return;

    if (enabled) {
      quantityContainer.removeAttribute('hidden');
      this.refs.addToCartButton.style.display = 'none';
    } else {
      quantityContainer.setAttribute('hidden', '');
      this.refs.addToCartButton.style.display = '';
    }
  }

  /**
   * Updates sticky quantity UI from a cart payload.
   * @param {Cart} cart - Cart payload.
   */
  #updateStickyQuantityFromCart(cart) {
    const currentVariantId = this.dataset.currentVariantId;
    if (!currentVariantId || !Array.isArray(cart?.items)) {
      this.#setStickyQuantityEnabled(false);
      return;
    }

    const cartItem = cart.items.find((item) => item.variant_id.toString() === currentVariantId.toString());
    const quantityInCart = cartItem?.quantity || 0;

    this.#setStickyQuantityEnabled(quantityInCart > 0);

    if (quantityInCart > 0) {
      this.#currentQuantity = Math.max(1, quantityInCart);
      this.#updateButtonText();
      this.#syncMainQuantityInput();
      return;
    }

    const defaultQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#currentQuantity = defaultQuantity;
    this.#updateButtonText();
    this.#syncMainQuantityInput();
  }

  /**
   * Fetches latest cart and syncs sticky quantity UI.
   */
  async #syncStickyQuantityFromCart() {
    try {
      const response = await fetch('/cart.js', { cache: 'no-store' });
      if (!response.ok) return;
      const cart = /** @type {Cart} */ (await response.json());
      this.#updateStickyQuantityFromCart(cart);
    } catch {
      // Keep default sticky behavior when cart fetch fails.
    }
  }

  /**
   * Syncs sticky quantity with the main product form quantity input.
   */
  #syncMainQuantityInput() {
    const productForm = this.#getProductForm();
    const quantityInput = /** @type {HTMLInputElement | null} */ (productForm?.querySelector('input[name="quantity"]'));
    if (!quantityInput) return;
    quantityInput.value = this.#currentQuantity.toString();
  }

  /**
   * Updates the cart quantity for the current sticky variant.
   * @param {number} quantity - Target quantity in cart.
   */
  async #updateCartQuantity(quantity) {
    if (themeQuantityControlsDisabled()) return;
    const targetQuantity = Math.max(0, quantity);
    this.#queuedCartQuantity = targetQuantity;
    const currentVariantId = this.dataset.currentVariantId;
    if (!currentVariantId) return;

    // Optimistically update the UI immediately.
    this.#applyOptimisticQuantity(targetQuantity);

    // If a request is in flight, keep only the latest desired quantity queued.
    if (this.#updatingCartQuantity) return;

    while (this.#queuedCartQuantity != null) {
      const nextQuantity = this.#queuedCartQuantity;
      this.#queuedCartQuantity = null;

      this.#updatingCartQuantity = true;

      try {
        const response = await fetch('/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            updates: {
              [currentVariantId]: nextQuantity,
            },
          }),
        });

        if (!response.ok) throw new Error(`Failed to update cart quantity: ${response.status}`);

        const cart = /** @type {Cart} */ (await response.json());
        this.#updateStickyQuantityFromCart(cart);

        this.dispatchEvent(
          new CartUpdateEvent(cart, this.id, {
            source: 'sticky-add-to-cart',
            productId: this.dataset.productId,
            variantId: currentVariantId,
            itemCount: nextQuantity,
          })
        );
      } catch (error) {
        console.error(error);
        await this.#syncStickyQuantityFromCart();
      } finally {
        this.#updatingCartQuantity = false;
      }
    }
  }

  /**
   * Optimistically applies quantity state before cart request completes.
   * @param {number} quantity - Desired cart quantity.
   */
  #applyOptimisticQuantity(quantity) {
    const defaultQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    const hasQuantity = quantity > 0;
    this.#setStickyQuantityEnabled(hasQuantity);
    this.#currentQuantity = hasQuantity ? quantity : defaultQuantity;
    this.#updateButtonText();
    this.#syncMainQuantityInput();
  }

  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    const defaultQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    const quantityInCart = parseInt(this.dataset.inCartQuantity || '0') || 0;

    this.#currentQuantity = quantityInCart > 0 ? quantityInCart : defaultQuantity;
    this.#setStickyQuantityEnabled(quantityInCart > 0);
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber, stickyQuantityValue } = this.refs;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }

    const stickyQuantityElement = /** @type {HTMLElement} */ (stickyQuantityValue);
    stickyQuantityElement.textContent = this.#currentQuantity.toString();
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
